import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import HeroSection from "../components/sections/HeroSection";
import TrustBand from "../components/sections/TrustBand";
import HowItWorks from "../components/sections/HowItWorks";
import ServiceCards from "../components/sections/ServiceCards";
import TestimonialCarousel from "../components/sections/TestimonialCarousel";
import { setPageMeta, localBusinessSchema, JsonLd } from "../lib/schema";
import { buildWhatsAppLink } from "../lib/whatsapp";

export default function HomePage() {
  useEffect(() => {
    setPageMeta({
      title: "Personal Shopper UK to Nigeria | My Private Shopper",
      description:
        "Premium UK-to-Nigeria personal shopping concierge. Selfridges, Harrods, Apple UK to your door in any of Nigeria's 36 states. 7–10 day delivery, trusted since 2011.",
      canonical: "https://privateshopper.co.uk/",
      image: "https://images.unsplash.com/photo-1609421600950-6b6f18b7e674?q=85&w=1600",
    });
  }, []);

  return (
    <>
      <JsonLd data={localBusinessSchema} />
      <HeroSection />
      <TrustBand />
      <HowItWorks />
      <ServiceCards />

      {/* Editorial banner */}
      <section className="bg-[#F8F5F0] py-24 md:py-32 border-t border-[#E5E0D8]">
        <div className="container-mps grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-6"
          >
            <img
              src="https://images.unsplash.com/photo-1627245084666-199325217d5b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHxsb25kb24lMjBtYXlmYWlyJTIwc3RyZWV0fGVufDB8fHx8MTc4MTM0NTQ1MHww&ixlib=rb-4.1.0&q=85"
              alt="London Mayfair"
              className="w-full h-[520px] object-cover"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-6"
          >
            <div className="overline text-[#C9A96E] mb-4">A Note From Our Founder</div>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-tight">
              We started MPS because{" "}
              <span className="italic text-[#C9A96E]">Nigerians deserved better</span>{" "}
              than broken UK checkouts.
            </h2>
            <p className="mt-8 text-[#6B7280] leading-relaxed">
              Every Nigerian shopper has been there — your card declined at the UK
              checkout, the courier refuses to ship, the freight cost is double the
              product. We built MPS to remove every one of those friction points.
              Every order is handled by a real person in London, photographed, and
              accountable end-to-end.
            </p>
            <Link
              to="/about"
              className="mt-10 inline-flex items-center gap-2 px-6 py-3 border border-[#1A1A1A] text-sm uppercase tracking-[0.18em] hover:bg-[#0A0A0A] hover:text-[#F8F5F0] transition-colors"
              data-testid="home-about-cta"
            >
              The MPS Story <ArrowUpRight size={14} />
            </Link>
          </motion.div>
        </div>
      </section>

      <TestimonialCarousel />

      {/* Final CTA */}
      <section className="bg-[#F8F5F0] py-24 md:py-32 text-center">
        <div className="container-mps max-w-3xl mx-auto">
          <div className="overline text-[#C9A96E] mb-4">Ready When You Are</div>
          <h2 className="font-serif text-4xl md:text-6xl tracking-tight">
            Your London concierge, <span className="italic text-[#C9A96E]">a tap away</span>.
          </h2>
          <p className="mt-8 text-[#6B7280] max-w-xl mx-auto leading-relaxed">
            Send us a product link, a screenshot, or just describe what you want.
            We&apos;ll quote you within hours.
          </p>
          <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/order"
              data-testid="home-final-cta-order"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#0A0A0A] text-[#F8F5F0] text-sm uppercase tracking-[0.2em] hover:bg-[#1A1A1A]"
            >
              Start Your Order
            </Link>
            <a
              href={buildWhatsAppLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="home-final-cta-whatsapp"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#25D366] text-[#0A0A0A] text-sm uppercase tracking-[0.2em] hover:bg-[#1EBE5C]"
            >
              Chat on WhatsApp
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
